import noop from 'lodash/noop';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DropAreaOptions, DropAreaState } from 'react-use/lib/useDrop';

const createProcess =
    (options: DropAreaOptions) => (dataTransfer: DataTransfer | null, event: DragEvent | ClipboardEvent) => {
        if (!dataTransfer) return;

        const uri = dataTransfer.getData('text/uri-list');

        if (uri) {
            (options.onUri || noop)(uri, event);

            return;
        }

        if (dataTransfer.files && dataTransfer.files.length) {
            (options.onFiles || noop)(Array.from(dataTransfer.files), event);

            return;
        }

        if ('clipboardData' in event && event.clipboardData) {
            const text = event.clipboardData.getData('text');

            (options.onText || noop)(text, event);

            return;
        }
    };

export const useDrop = (options: DropAreaOptions = {}): DropAreaState => {
    const { onFiles, onText, onUri } = options;
    const [over, setOver] = useState<boolean>(false);
    const process = useMemo(() => createProcess({ onFiles, onText, onUri }), [onFiles, onText, onUri]);
    const dragCounter = useRef(0);

    useEffect(() => {
        const onDragEnter = (event: DragEvent) => {
            event.preventDefault();
            dragCounter.current += 1;
            setOver(true);
        };

        const onDragLeave = (event: DragEvent) => {
            event.preventDefault();
            dragCounter.current -= 1;

            if (dragCounter.current === 0) {
                setOver(false);
            }
        };

        const onDrop = (event: DragEvent) => {
            event.preventDefault();
            dragCounter.current = 0;
            setOver(false);
            process(event.dataTransfer, event);
        };

        const onPaste = (event: ClipboardEvent) => {
            process(event.clipboardData, event);
        };

        document.addEventListener('dragenter', onDragEnter);
        document.addEventListener('dragleave', onDragLeave);
        document.addEventListener('drop', onDrop);

        if (onText) {
            document.addEventListener('paste', onPaste);
        }

        return () => {
            document.removeEventListener('dragenter', onDragEnter);
            document.removeEventListener('dragleave', onDragLeave);
            document.removeEventListener('drop', onDrop);
            document.removeEventListener('paste', onPaste);
        };
    }, [onText, process]);

    return { over };
};
